import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { UserRoleEnum, useUserContext } from '../../shared/contexts';
import { LayoutShell } from '../../shared/layouts';
import { IBusinessView, IEmployeeView, businessAdminService, businessService, employeeService } from '../../shared/services';
import { Forbidden } from '../../shared/components/SconApp/forbidden/Forbidden';
import { EntitySelector, Scheduler, SchedulerFilterOptionsEnum } from '../../shared/components';

export const Schedule: React.FC = () => {
  const { userId, userRoles } = useUserContext();
  const isReceptionist = userRoles.includes(UserRoleEnum.Receptionist);

  const { data: employee, refetch: refetchEmployee } = useQuery(
    ['employee'],
    () => employeeService.getByUserId(userId),
    { enabled: userRoles.includes(UserRoleEnum.Employee) }
  );

  const { data: businessAdmins } = useQuery(
    ['businessAdmin'],
    () => businessAdminService.getByUserId(userId),
    { enabled: userRoles.includes(UserRoleEnum.BusinessAdmin) }
  );

  const businessAdmin = businessAdmins?.[0];

  // --- Receptionist: business selector state ---
  const [receptionistBusinessId, setReceptionistBusinessId] = useState<number>();

  const receptionistBusinessOptions = employee?.businessEmployees?.map(be => ({
    id: be.businessId,
    name: be.business.name
  })) || [];

  // Auto-select if only one business
  useEffect(() => {
    if (isReceptionist && receptionistBusinessOptions.length === 1 && !receptionistBusinessId) {
      setReceptionistBusinessId(receptionistBusinessOptions[0].id);
    }
  }, [isReceptionist, receptionistBusinessOptions, receptionistBusinessId]);

  // --- Determine which business ID to use ---
  const businessId = businessAdmin?.businessId ?? receptionistBusinessId;

  const { data: business, refetch: refetchBusiness } = useQuery(
    ['business', businessId],
    () => businessId ? businessService.getById(businessId) : Promise.reject('No business'),
    { enabled: !!businessId }
  );

  const isEmployee = userRoles.includes(UserRoleEnum.Employee);
  const isBusinessAdmin = userRoles.includes(UserRoleEnum.BusinessAdmin);

  // Receptionists see the business schedule view (with professional filter)
  const showAsBusinessAdmin = (isBusinessAdmin && business) || (isReceptionist && business);
  const showAsEmployee = isEmployee && employee && !isBusinessAdmin && !isReceptionist;

  if (!isEmployee && !isBusinessAdmin) {
    return <Forbidden />;
  }

  const parseHour = (timeStr?: string | null): number | null => {
    if (!timeStr) return null;
    const h = parseInt(timeStr.split(':')[0], 10);
    return isNaN(h) ? null : h;
  };

  const getStartTime = (data: IEmployeeView | IBusinessView): number => {
    if ('businessEmployees' in data) {
      // Employee view: earliest start across all business employees' weekly schedules
      const hours = data.businessEmployees
        .flatMap(be => be.weeklySchedules ?? [])
        .map(s => parseHour(s.startTime))
        .filter((h): h is number => h !== null);
      return hours.length > 0 ? Math.min(...hours) : 8;
    }
    // Business view: use the business's own weekly schedules
    const hours = (data.weeklySchedules ?? [])
      .map(s => parseHour(s.startTime))
      .filter((h): h is number => h !== null);
    return hours.length > 0 ? Math.min(...hours) : 8;
  };

  const getEndTime = (data: IEmployeeView | IBusinessView): number => {
    if ('businessEmployees' in data) {
      // Employee view: latest end across all business employees' weekly schedules
      const hours = data.businessEmployees
        .flatMap(be => be.weeklySchedules ?? [])
        .map(s => parseHour(s.endTime))
        .filter((h): h is number => h !== null);
      // Add 1 so the scheduler extends past the last appointment slot
      return hours.length > 0 ? Math.max(...hours) + 1 : 19;
    }
    // Business view: use the business's own weekly schedules
    const hours = (data.weeklySchedules ?? [])
      .map(s => parseHour(s.endTime))
      .filter((h): h is number => h !== null);
    return hours.length > 0 ? Math.max(...hours) + 1 : 19;
  };

  const renderEmployeeSchedule = (emp: IEmployeeView) => (
    <Scheduler
      filterLabel='Estabelecimento'
      filterOptions={emp.businessEmployees?.map(item => ({
        id: item.business.id,
        name: item.business.name
      })) || []}
      filterOptionType={SchedulerFilterOptionsEnum.Business}
      startTime={getStartTime(emp)}
      endTime={getEndTime(emp)}
      refetch={refetchEmployee}
    />
  );

  const renderBusinessSchedule = (biz: IBusinessView) => (
    <Scheduler
      businessId={biz.id}
      filterLabel='Profissional'
      filterOptions={biz.employees?.map(item => ({
        id: item.id,
        name: item.name
      })) || []}
      employees={biz.employees?.map(item => ({
        id: item.id,
        name: item.name,
        profilePicture: item.profilePicture,
        color: item.color,
      }))}
      filterOptionType={SchedulerFilterOptionsEnum.Employee}
      startTime={getStartTime(biz)}
      endTime={getEndTime(biz)}
      refetch={refetchBusiness}
    />
  );

  return (
    <LayoutShell>
      {isReceptionist && receptionistBusinessOptions.length > 1 && (
        <Box sx={{ mb: 1, minWidth: 250, px: 2 }}>
          <EntitySelector
            options={receptionistBusinessOptions}
            value={receptionistBusinessId}
            onChange={setReceptionistBusinessId}
            label='Estabelecimento'
          />
        </Box>
      )}
      {showAsBusinessAdmin && renderBusinessSchedule(business)}
      {showAsEmployee && renderEmployeeSchedule(employee)}
    </LayoutShell>
  );
};
